/**
 * Shared display constants/helpers for the Guardrails console.
 *
 * NOTE on match_type: the DB is the source of truth (quill-app migrations
 * 474/475). Rules are authored as 'fast_path_keyword' | 'fast_path_embedding'
 * | 'classifier_required'; the decisions log additionally records
 * 'classifier_llm' and 'admin_override'. Labels below cover both unions plus
 * the 'none' value the /test sandbox endpoint can return.
 */

import { colors } from '../../../theme/designTokens';

export const VERDICT_COLOR: Record<string, { bg: string; text: string; border: string }> = {
  on_mission: { bg: colors.success[50], text: colors.success.dark, border: colors.success.light },
  off_mission: { bg: colors.error[50], text: colors.error.dark, border: colors.error.light },
  requires_classifier: { bg: colors.warning[50], text: colors.warning.dark, border: colors.warning.light },
  allowed_override: { bg: colors.deepTeal[50], text: colors.deepTeal[600], border: colors.deepTeal[300] },
};

const DEFAULT_VERDICT_COLOR = { bg: colors.gray[100], text: colors.gray[700], border: colors.gray[300] };

export function verdictColor(verdict: string | null | undefined): { bg: string; text: string; border: string } {
  if (!verdict) return DEFAULT_VERDICT_COLOR;
  return VERDICT_COLOR[verdict] ?? DEFAULT_VERDICT_COLOR;
}

export function verdictLabel(verdict: string | null | undefined): string {
  if (!verdict) return '—';
  return verdict
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Rule-authoring verdict options (what a guardrail rule can assert). */
export const RULE_VERDICTS = ['on_mission', 'off_mission', 'requires_classifier'] as const;

/** Decisions-log verdict filter options (what the live engine actually logged). */
export const DECISION_VERDICTS = ['on_mission', 'off_mission', 'allowed_override'] as const;

/** Rule-authoring match types (rules-table CHECK, migration 474). */
export const MATCH_TYPES = ['fast_path_keyword', 'fast_path_embedding', 'classifier_required'] as const;

export function matchTypeLabel(matchType: string | null | undefined): string {
  switch (matchType) {
    case 'fast_path_keyword':
      return 'Fast Path (Keyword)';
    case 'fast_path_embedding':
      return 'Fast Path (Embedding)';
    case 'classifier_required':
      return 'Classifier Required';
    case 'classifier_llm':
      return 'LLM Classifier';
    case 'admin_override':
      return 'Admin Override';
    case 'none':
      return 'No fast-path match';
    default:
      return matchType ?? '—';
  }
}

/** Best-effort coercion of the JSONB capability_categories column to a string[]. */
export function categoriesToArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string');
  }
  return [];
}

export function truncateId(id: string | null | undefined, len = 8): string {
  if (!id) return '—';
  return id.length > len ? `${id.slice(0, len)}…` : id;
}
